import type {
  DocumentsUploadResult,
  UploadedDocuments,
} from "Api/Mutations/Onboarding/useUploadIdentityDocuments";
import { DocumentsDeclinedInfo } from "Components/Onboarding/shared/DocumentsUpload/DocumentsDeclinedInfo";
import { DocumentsUpload } from "Components/Onboarding/shared/DocumentsUpload/DocumentsUpload";
import { ExistingDocumentsInfo } from "Components/Onboarding/shared/DocumentsUpload/ExistingDocumentsInfo";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";

export type DocumentsStepParty = {
  publicID: string;
  hasDocumentsUploaded: boolean;
};

type Props = {
  /** Undefined until the queries have loaded the party. */
  party: DocumentsStepParty | undefined;
  isLoading: boolean;
  isSecondDocumentRequired: boolean;
  onNextStepRequested: () => void;
};

type View =
  | { kind: "initial" }
  | { kind: "form" }
  | {
      kind: "declined";
      result: DocumentsUploadResult;
      documents: UploadedDocuments;
    };

/**
 * The upload form, the "already uploaded" skip, or the declined panel. The parent renders this unconditionally
 * and a result is shown before anything else, so the refetch the upload triggers (after which the party already
 * has documents) can neither unmount the result nor replace the panel with the skip.
 */
export const DocumentsStepContent = ({
  party,
  isLoading,
  isSecondDocumentRequired,
  onNextStepRequested,
}: Props) => {
  const { t } = useResource();
  const [view, setView] = useState<View>({ kind: "initial" });

  if (view.kind === "declined") {
    return (
      <DocumentsDeclinedInfo
        checks={view.result.documentChecks}
        attemptsLeft={view.result.verificationAttemptsLeft}
        documents={view.documents}
        onRetry={() => setView({ kind: "form" })}
        onContinue={onNextStepRequested}
      />
    );
  }

  if (!party) {
    return isLoading ? (
      <CenteredCircularProgress />
    ) : (
      <ErrorText>
        {t(Resources.Onboarding.Steps.Documents.LoadingError)}
      </ErrorText>
    );
  }

  if (view.kind === "initial" && party.hasDocumentsUploaded) {
    return <ExistingDocumentsInfo onNextStepRequested={onNextStepRequested} />;
  }

  return (
    <DocumentsUpload
      onNextStepRequested={onNextStepRequested}
      owningPartyPublicID={party.publicID}
      isSecondDocumentRequired={isSecondDocumentRequired}
      onDeclined={(result, documents) =>
        setView({ kind: "declined", result, documents })
      }
    />
  );
};
